import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { isRtlLocale } from "@ticketnjoy/types";

import { ThemeProvider } from "./theme-provider";
import { DialogProvider } from "@/components/dialog-provider";
import { Toaster } from "@/components/ui/sonner";
import { meQueryKey } from "@/features/organizers/api";
import { ApiError } from "@/lib/api-client";
import i18n, { useLocale } from "@/lib/i18n";

/**
 * Oturum ortada düşerse (401) `me` sıfırlanır → ProtectedRoute login'e yönlendirir.
 * Eski `me` verisi tutulsaydı guard sayfayı açık bırakırdı. Diğer sorgular da
 * silinir: aynı sekmede başka hesapla girilince önceki organizatörün verisi görünmesin.
 */
function handleUnauthorized(client: QueryClient, error: unknown) {
  if (!(error instanceof ApiError) || error.status !== 401) return;
  if (client.getQueryData(meQueryKey) === undefined) return;

  toast.error(i18n.t("session.expired"));
  client.resetQueries({ queryKey: meQueryKey });
  client.removeQueries({
    predicate: (q) => q.queryKey[0] !== meQueryKey[0] || q.queryKey[1] !== meQueryKey[1],
  });
}

function createQueryClient() {
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({ onError: (error) => handleUnauthorized(client, error) }),
    mutationCache: new MutationCache({ onError: (error) => handleUnauthorized(client, error) }),
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000,
        retry: 1,
        refetchOnWindowFocus: true,
      },
    },
  });
  return client;
}

export function AppProviders({ children }: { children: ReactNode }) {
  const [client] = useState(createQueryClient);
  const isRtl = isRtlLocale(useLocale());

  return (
    <ThemeProvider>
      <QueryClientProvider client={client}>
        <DialogProvider>{children}</DialogProvider>
        <Toaster
          position={isRtl ? "top-left" : "top-right"}
          dir={isRtl ? "rtl" : "ltr"}
          containerAriaLabel={i18n.t("common.notifications")}
          richColors
        />
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ThemeProvider>
  );
}
